import { useState } from 'react';

import { toast } from 'react-toastify';
import styled from 'styled-components';

import {
    SupplierState,
    useGetSuppliersQuery,
    useCreateSupplyOrderMutation,
    TotadminPermission,
} from 'data/__generated__';

import { Loader, LoaderModeType } from 'components/Loader';
import { TotemPopup } from 'components/TotemPopup';
import { TotemPrimaryLoadingButton } from 'components/TotemPrimaryLoadingButton';
import { TotemSelect } from 'components/TotemSelect';
import { useNavigate } from 'react-router-dom';
import { WarehouseColumnSelect } from '@/components/Select/WarehouseColumnSelect';

export function CreateSupplyOrderPopup({ isOpen, setIsOpen }: { isOpen: boolean; setIsOpen: (arg: boolean) => void }) {
    const navigate = useNavigate();
    const [selectedSupplierId, setSelectedSupplierId] = useState<string | null>(null);
    const [selectedWarehouseColumnId, setSelectedWarehouseColumnId] = useState<string | null>(null);
    const { loading: suppliersLoading, data: suppliersData, error: suppliersError } = useGetSuppliersQuery();

    const [createSupplyOrder, { loading: creationLoading }] = useCreateSupplyOrderMutation();

    async function handleSubmit() {
        if (!selectedWarehouseColumnId) {
            toast.error('Veuillez sélectionner un entrepôt');
            return;
        }

        const { data } = await createSupplyOrder({
            variables: {
                supplierId: selectedSupplierId ?? '',
                warehouseColumnId: selectedWarehouseColumnId,
            },
        });

        if (!data) {
            throw new Error('Une erreur est survenue lors de la suppression des achats');
        }

        const {
            createSupplyOrderMutation: { createdSupplyOrder, errors, warnings },
        } = data;

        if (errors.length) {
            errors.forEach((error, index) => {
                toast.error(<span key={index}>Erreur : {error}</span>, { autoClose: false });
            });
        }
        if (warnings.length) {
            warnings.forEach((warning, index) => {
                toast.warning(<span key={index}>Warning : {warning}</span>, { autoClose: false });
            });
        }
        if (errors.length + warnings.length > 1) {
            toast.info('Cliquez pour fermer toutes les notifications', {
                autoClose: false,
                onClick: () => toast.dismiss(),
            });
        }

        if (createdSupplyOrder) {
            navigate(`/supply-order/${createdSupplyOrder._id}`);
        }

        setIsOpen(false);
    }

    if (suppliersLoading) {
        return (
            <TotemPopup title="Créer un achat" isOpen={isOpen} setIsOpen={setIsOpen}>
                Chargement des fournisseurs
                <Loader size="20px" mode={LoaderModeType.Spin} />
            </TotemPopup>
        );
    }

    if (suppliersError || !suppliersData) {
        throw new Error('Une erreur est survenue lors de la récupération des fournisseurs');
    }

    const supplierOptions = suppliersData.suppliers
        .filter(({ state }) => state !== SupplierState.Archived)
        .map((supplier) => ({ value: supplier._id, label: supplier.name }))
        .sort((supplierOptionA, supplierOptionB) =>
            supplierOptionA.label.toLowerCase().localeCompare(supplierOptionB.label.toLowerCase()),
        );

    return (
        <TotemPopup title="Créer un achat" isOpen={isOpen} setIsOpen={setIsOpen} contentOverflow="visible">
            Veuillez sélectionner le fournisseur pour lequel vous voulez créer l'achat :
            <SubmitContainer>
                <TotemSelect
                    label="Fournisseur"
                    dataTest="supplier-select"
                    placeholder="Sélectionner un fournisseur (recherche par nom ou par _id)"
                    value={selectedSupplierId}
                    options={supplierOptions}
                    onChange={setSelectedSupplierId}
                />
                <WarehouseColumnSelect
                    dataTest="warehouse-column-select"
                    value={selectedWarehouseColumnId}
                    onChange={setSelectedWarehouseColumnId}
                />
                <ValidationButton
                    loading={suppliersLoading || creationLoading}
                    useIcon={false}
                    label="Confirmer"
                    onClick={handleSubmit}
                    requiredPermissions={[TotadminPermission.SupplyOrdersManage]}
                />
            </SubmitContainer>
        </TotemPopup>
    );
}

const SubmitContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    margin-top: 15px;
`;

const ValidationButton = styled(TotemPrimaryLoadingButton)`
    margin-top: 15px;
`;
